import { DeviceDTO } from '@eppendorf/vnls-device-service-utils';
import { FunctionComponentElement } from 'react';

import { queryClient } from '$shared/query/query-client';

import { ErrorScreen } from '$features/add-device/device-dialog/screens/error-screen';
import { SubmitScreen } from '$features/add-device/device-dialog/screens/submit-screen';
import { SuccessScreen } from '$features/add-device/device-dialog/screens/success-screen';
import { WaitingScreen } from '$features/add-device/device-dialog/screens/waiting-screen';
import {
  useReregisterDevice,
  deviceUsageQueryKey,
  devicesQueryKey,
} from '$features/devices/devices.api';

export type AddDeviceDialogScreens =
  | 'submitScreen'
  | 'waitingScreen'
  | 'successScreen'
  | 'errorScreen';

export interface DeviceDialogContentProps {
  activeScreen: AddDeviceDialogScreens;
  serialNumber?: string;
  onSerialNumberChange: (serialNumber: string) => void;
  setActiveScreen: (screen: AddDeviceDialogScreens) => void;
  device?: DeviceDTO;
}

export function DeviceDialogContent({
  activeScreen,
  serialNumber,
  onSerialNumberChange,
  setActiveScreen,
  device,
}: DeviceDialogContentProps): FunctionComponentElement<DeviceDialogContentProps> {
  const { mutateAsync: reregisterDevice } = useReregisterDevice();

  function navigateToWaitingScreen(serial: string): void {
    // Clear any cached device data for this serial number before showing waiting screen
    // This ensures the WaitingScreen query starts fresh and waits for actual device pairing
    queryClient.removeQueries({
      queryKey: [devicesQueryKey, 'Eppendorf', serial],
    });

    setActiveScreen('waitingScreen');
    onSerialNumberChange(serial);
  }

  function navigateToAddedScreen(): void {
    setActiveScreen('successScreen');
  }

  function navigateToErrorScreen(): void {
    setActiveScreen('errorScreen');
  }

  function tryAgain(): void {
    if (!serialNumber) {
      return;
    }

    reregisterDevice({ manufacturer: 'Eppendorf', serialNumber });
    setActiveScreen('waitingScreen');
  }

  const onSubmit = (serial: string) => {
    navigateToWaitingScreen(serial);
    queryClient.invalidateQueries({ queryKey: deviceUsageQueryKey });
  };

  return (
    <>
      {activeScreen === 'submitScreen' && <SubmitScreen onSubmit={onSubmit} />}
      {activeScreen === 'waitingScreen' && serialNumber && (
        <WaitingScreen
          onSubmit={() => navigateToAddedScreen()}
          serialNumber={serialNumber}
          onError={() => navigateToErrorScreen()}
        />
      )}
      {activeScreen === 'errorScreen' && serialNumber && (
        <ErrorScreen onSubmit={() => tryAgain()} device={device} />
      )}
      {activeScreen === 'successScreen' && <SuccessScreen device={device} />}
    </>
  );
}
